import { push } from 'connected-react-router';
import * as React from 'react';
import { connect } from 'react-redux';

import './Player.css';

import Timestamp from '../Timestamp';
import {
  BackButton,
  DownloadButton,
  ForwardButton,
  PlayButton,
  TranscriptButton,
  UnfoldButton,
} from './Buttons';
import Waveform from './Waveform';

import {
  setPlaying,
  togglePlaybackRate,
  toggleWaveform,
} from '../../actions';
import { IAlignedWord } from '../../api';
import {
  audioBack,
  audioForward,
  downloadTrack,
  playClip,
} from '../../thunks';
import { RootState } from '../../types';

interface IProps {
  alignment: IAlignedWord[],
  currentTime: number,
  duration: number | null,
  isPlaying: boolean,
  isSeeking: boolean,
  playbackRate: number,
  recordingId: string | null,
  showWaveform: boolean,

  onBack: () => any,
  onExpand: () => any,
  onForward: () => any,
  onSeek: (t: number) => any,
  onSetPlaying: (playing: boolean) => any,
  onToggleRate: () => any,
  onTranscript: (id: string) => any,
  onDownload: (id: string) => any,
}

interface ILoaderProps {
  disabled: boolean,
}

const Loader: React.SFC<ILoaderProps> = (props: ILoaderProps) => (
  <div className={ 'loader' + (props.disabled ? '' : ' on') }>
    <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" stroke="#fff">
        <g fill="none" fillRule="evenodd" strokeWidth="2">
            <circle cx="22" cy="22" r="1">
                <animate attributeName="r"
                    begin="0s" dur="1.8s"
                    values="1; 20"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.165, 0.84, 0.44, 1"
                    repeatCount="indefinite" />
                <animate attributeName="stroke-opacity"
                    begin="0s" dur="1.8s"
                    values="1; 0"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.3, 0.61, 0.355, 1"
                    repeatCount="indefinite" />
            </circle>
            <circle cx="22" cy="22" r="1">
                <animate attributeName="r"
                    begin="-0.9s" dur="1.8s"
                    values="1; 20"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.165, 0.84, 0.44, 1"
                    repeatCount="indefinite" />
                <animate attributeName="stroke-opacity"
                    begin="-0.9s" dur="1.8s"
                    values="1; 0"
                    calcMode="spline"
                    keyTimes="0; 1"
                    keySplines="0.3, 0.61, 0.355, 1"
                    repeatCount="indefinite" />
            </circle>
        </g>
    </svg>
  </div>
)

class Player extends React.Component<IProps> {

  public render() {
    const {
      alignment,
      currentTime,
      duration,
      isPlaying,
      isSeeking,
      onBack,
      onExpand,
      onForward,
      onSeek,
      onToggleRate,
      playbackRate,
      recordingId,
      showWaveform,
    } = this.props;
    const disabled = !recordingId;

    return (<div className="player">
              <Waveform
                        alignment={ alignment }
                        currentTime={ currentTime }
                        isSeeking={ isSeeking }
                        duration={ duration }
                        recordingId={ recordingId }
                        isPlaying={ isPlaying }
                        onSeek={ onSeek }
                        expanded={ showWaveform } />
              <div className="controls">
                <BackButton
                            disabled={ disabled }
                            onClick={ onBack } />
                <PlayButton
                            disabled={ disabled }
                            onClick={ this.togglePlaying }
                            playing={ isPlaying } />
                <ForwardButton
                               disabled={ disabled }
                               onClick={ onForward } />
                <Loader disabled={ !isSeeking } />
                <div className="timing">
                  <Timestamp currentTime={ currentTime } />
                  { " / " }
                  <Timestamp currentTime={ duration } />
                </div>

                <button onClick={onToggleRate}>
                {playbackRate}x
                </button>
                
                <TranscriptButton
                                  disabled={ disabled }
                                  onClick={ this.onClickTranscript } />
                <DownloadButton
                  disabled={disabled}
                  onClick={this.onDownload} />
                <UnfoldButton
                              disabled={ disabled }
                              expanded={ showWaveform }
                              onClick={ onExpand } />
              </div>
            </div>);
  }

  private onClickTranscript = () => {
    const {
      onTranscript,
      recordingId,
    } = this.props;
    if (!recordingId) {
      return;
    }
    onTranscript(recordingId);
  }

  private onDownload = () => {
    const {
      onDownload,
      recordingId,
    } = this.props;
    if (!recordingId) {
      return;
    }
    onDownload(recordingId);
  }

  private togglePlaying = () => {
    const {
      isPlaying,
      onSetPlaying,
    } = this.props;
    onSetPlaying(!isPlaying);
  }

}

export const PlayerContainer = connect(
  ({
    player,
    tracks,
    transcripts,
  }: RootState) => {
    const {
      currentTime,
      isPlaying,
      isSeeking,
      playbackRate,
      recordingId,
      showWaveform,
    } = player;

    const track = recordingId ? tracks.data[recordingId] : null;
    const duration = track ? track.duration_sec : null;

    const tran = recordingId ? transcripts.data[recordingId] : null;
    const alignment = tran ? tran.alignment : [];

    return {
      alignment,
      currentTime,
      duration,
      isPlaying,
      isSeeking,
      playbackRate,
      recordingId,
      showWaveform,
    };
  },
  dispatch => ({
    onBack: () => dispatch<any>(audioBack()),
    onDownload: (trackID: string) => dispatch<any>(downloadTrack(trackID)),
    onExpand: () => dispatch<any>(toggleWaveform()),
    onForward: () => dispatch<any>(audioForward()),
    onSeek: (t: number) => dispatch<any>(playClip({ start: t })),
    onSetPlaying: (playing: boolean) => dispatch<any>(setPlaying(playing)),
    onToggleRate: () => dispatch(togglePlaybackRate()),
    onTranscript: (trackID: string) => dispatch<any>(push(`/tracks/${trackID}`)),
  })
)(Player)
export default PlayerContainer;



// WEBPACK FOOTER //
// ./src/components/Player/Player.tsx